/**
 * semanaLlenaDemo.js — la semana de ejemplo LLENA del simulador de autoescuela (9/10/2026, David, para las demos:
 * «que cada profesor tenga la semana bastante llena»).
 *
 * Añade a los datos de ejemplo (mockData.js → SECTOR_DATA.autoescuela) 18 alumnos más (6 por profesor, con su
 * profesor habitual) y, sobre las prácticas que ya había, las que faltan para que cada profesor tenga de 4 a 6
 * prácticas al día de lunes a viernes, con huecos realistas (mañana y tarde, dentro de su horario de Trabajadores),
 * tipos variados (ciudad, carretera, autovía), un circuito cerrado de grupo por profesor y alguna cancelada. Todo
 * relativo a la semana actual: al abrir el simulador siempre se ve lleno.
 *
 * Reglas para no descuadrar el resto de pantallas:
 *  - Las prácticas nuevas solo llevan alumnos NUEVOS: las fichas, chats, exámenes y aprobados de los alumnos de
 *    siempre no cambian.
 *  - Los ids son `pr-sem-…` (los chats de ejemplo solo leen `pr-<número>`) y se añaden AL FINAL de la lista
 *    (practicasDemo.js reparte el estado de aviso por posición).
 *  - Nunca se pisan: ni el mismo profesor, ni el mismo coche, ni el mismo alumno a la misma hora, ni con las de antes
 *    (tampoco con sus canceladas: «Reactivar» no debe chocar).
 *  - Las ya dadas llevan casi todas las dos firmas (si no, «Sin firmar» saldría con medio calendario); la de
 *    «no asistió» solo si ya ha pasado al abrir el simulador.
 * La confirmación (✓ verde / «Sin confirmar») la pone practicasDemo.js para toda la semana.
 *
 * PURO salvo la hora: sin imports de otras pantallas (mockData lo importa al cargar).
 */

const LETRAS_DNI = "TRWAGMYFPDXBNJZSQVHLCKE";
const dni = (n) => `${n}${LETRAS_DNI[n % 23]}`;
const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// Alumnos de la semana llena: 6 por profesor. Los de Miguel Ángel con `A2` hacen el circuito cerrado de moto.
const ALUMNOS = [
  // Carlos Navarro (inst-001) · sede centro
  ["alu-101", "inst-001", "Nerea Gil Ortega", "B", 14, 9, "2005-02-11", "Muy segura en ciudad; repasar glorietas grandes"],
  ["alu-102", "inst-001", "Iván Blanco Rey", "B", 6, 4, "2006-08-03", "Le cuesta el embrague en pendiente"],
  ["alu-103", "inst-001", "Claudia Vidal Soler", "B", 18, 13, "2004-12-19", "Lista para examen en dos semanas"],
  ["alu-104", "inst-001", "Marcos Prieto León", "B", 9, 6, "2003-06-27", ""],
  ["alu-105", "inst-001", "Aitana Serrano Cruz", "B", 4, 3, "2006-10-02", "Recién empezada"],
  ["alu-106", "inst-001", "Rubén Molina Paz", "B", 11, 8, "2002-03-14", "Trabaja por la tarde: solo mañanas"],
  // Ana Belén Ruiz (inst-002) · sede norte
  ["alu-107", "inst-002", "Laura Campos Vera", "B", 16, 12, "2005-09-30", "Preparando autovía"],
  ["alu-108", "inst-002", "Diego Romero Sáez", "B", 7, 5, "2006-01-22", ""],
  ["alu-109", "inst-002", "Paula Navas Ibáñez", "B", 12, 9, "2004-04-08", "Mejorar el aparcamiento en línea"],
  ["alu-110", "inst-002", "Jorge Rubio Mena", "B", 3, 2, "2007-02-15", "Primeras clases"],
  ["alu-111", "inst-002", "Alba Herrera Cano", "B", 20, 15, "2003-11-05", "Examen práctico pronto"],
  ["alu-112", "inst-002", "Óscar Lozano Gil", "B", 8, 6, "2001-07-17", "Solo tardes"],
  // Miguel Ángel Torres (inst-003) · sede sur
  ["alu-113", "inst-003", "Carla Peña Ruiz", "B", 10, 7, "2005-05-21", ""],
  ["alu-114", "inst-003", "Mario Esteban Sanz", "A2", 5, 4, "2004-08-29", "Moto: circuito cerrado y luego vías abiertas"],
  ["alu-115", "inst-003", "Noelia Fuentes Mora", "B", 13, 10, "2006-03-12", "Repasar incorporaciones a autovía"],
  ["alu-116", "inst-003", "Víctor Pastor Gómez", "A2", 7, 5, "2003-01-09", "Moto: equilibrio a baja velocidad"],
  ["alu-117", "inst-003", "Lidia Marín Rojas", "B", 2, 1, "2007-06-25", "Recién matriculada"],
  ["alu-118", "inst-003", "Samuel Cortés Ríos", "A2", 9, 7, "2002-10-30", "Moto: listo para circular por ciudad"],
];
const SEDE_DE = { "inst-001": "centro", "inst-002": "norte", "inst-003": "sur" };

/** Los 18 alumnos nuevos, con la misma forma que los de mockData. */
export function alumnosSemanaLlena() {
  return ALUMNOS.map(([alumno_id, instructor_id, name, carnet_type, horas, completadas, nacimiento, obs], i) => ({
    alumno_id,
    instructor_id,
    name,
    status: "activo",
    phone: `+346111001${pad(i + 1)}`,
    dni: dni(48210300 + i * 7919),
    carnet_type,
    horas_acumuladas: horas,
    practicas_completadas: completadas,
    fecha_nacimiento: nacimiento,
    observaciones: obs,
    sede_id: SEDE_DE[instructor_id],
  }));
}

// Horario de fábrica de cada profesor (el de profesoresStore.FACTORY_SCHEDULES, en minutos desde las 00:00).
const HORARIO = { "inst-001": [8 * 60, 20 * 60], "inst-002": [9 * 60, 20 * 60], "inst-003": [8 * 60, 20 * 60] };
const COCHE_DE = { "inst-001": "veh-001", "inst-002": "veh-002", "inst-003": "veh-003" };

// Huecos posibles de un día: [inicio, minutos, tipo]. Cinco modelos de día (mañana y tarde, con algún hueco libre
// entre medias, como una semana real); cada profesor empieza por uno distinto.
const M = (h, m = 0) => h * 60 + m;
const DIAS_MODELO = [
  [[M(8), 60, "ciudad"], [M(9), 60, "ciudad"], [M(10, 30), 90, "carretera"], [M(12), 60, "autovia"], [M(16), 60, "ciudad"], [M(17), 60, "ciudad"], [M(18, 30), 60, "ciudad"]],
  [[M(8, 30), 60, "ciudad"], [M(10), 60, "ciudad"], [M(11), 60, "autovia"], [M(13), 60, "ciudad"], [M(16, 30), 60, "ciudad"], [M(17, 30), 60, "carretera"], [M(19), 60, "ciudad"]],
  [[M(9), 60, "ciudad"], [M(10), 90, "carretera"], [M(12), 60, "ciudad"], [M(13), 60, "ciudad"], [M(16, 30), 60, "autovia"], [M(18), 60, "ciudad"], [M(19), 60, "ciudad"]],
  [[M(8), 60, "ciudad"], [M(9, 30), 60, "autovia"], [M(11, 30), 60, "ciudad"], [M(12, 30), 60, "ciudad"], [M(16), 90, "carretera"], [M(18), 60, "ciudad"], [M(19), 60, "ciudad"]],
  [[M(9), 60, "ciudad"], [M(10), 60, "ciudad"], [M(12), 60, "carretera"], [M(13), 60, "ciudad"], [M(17), 60, "ciudad"], [M(18), 60, "autovia"], [M(19), 60, "ciudad"]],
];
// Prácticas al día por profesor (lunes a viernes), contando las que ya había: entre 4 y 6.
const OBJETIVO = { "inst-001": [6, 5, 5, 6, 5], "inst-002": [5, 6, 5, 5, 6], "inst-003": [5, 5, 6, 5, 6] };
// Circuito cerrado de grupo (varios alumnos a la vez), uno por profesor: [día 0=lunes, inicio, minutos, alumnos].
const CIRCUITOS = {
  "inst-001": [3, M(17, 30), 90, ["alu-102", "alu-105"], "Circuito cerrado (pista): maniobras en grupo"],
  "inst-002": [1, M(13), 90, ["alu-108", "alu-110"], "Circuito cerrado (pista): maniobras en grupo"],
  "inst-003": [2, M(11), 90, ["alu-114", "alu-116", "alu-118"], "Circuito cerrado de moto A2 (pista)"],
};
// Canceladas de la semana: [profesor, día, nº de hueco nuevo de ese día, motivo, quién].
const CANCELADAS = [
  ["inst-002", 1, 1, "Tiene un examen en el instituto", "alumno"],
  ["inst-001", 4, 2, "Coche en el taller", "autoescuela"],
  ["inst-003", 0, 3, "Se encuentra mal", "alumno"],
];

const ms = (p) => [new Date(p.start).getTime(), new Date(p.end).getTime()];
const solapa = (a0, a1, b0, b1) => a0 < b1 && b0 < a1;
const alumnosDe = (p) => (Array.isArray(p.alumno_ids) && p.alumno_ids.length ? p.alumno_ids : [p.alumno_id]).filter(Boolean);

/**
 * Prácticas que llenan la semana. `base` = lunes (dayjs) de mockData; `existentes` = las de antes (para no pisarlas);
 * `now` = la hora de la demo (lo ya pasado puede ir firmado o como «no asistió»).
 */
export function practicasSemanaLlena(base, existentes = [], now = Date.now()) {
  const vivas = existentes.filter((p) => p && p.status !== "cancelada");
  const out = [];
  // Huecos del profesor ya cogidos: también los de sus canceladas de antes (se pueden reactivar sin pisarse).
  const ocupadas = (inst) => [...existentes.filter(Boolean), ...out.filter((p) => p.status !== "cancelada")].filter((p) => p.instructor_id === inst);
  const libreAlumno = (id, t0, t1) => ![...vivas, ...out].some((p) => p.status !== "cancelada" && alumnosDe(p).includes(id) && solapa(t0, t1, ...ms(p)));
  const nueva = (inst, dia, inicio, minutos, tipo, alumno_ids, extra = {}) => {
    const d = base.add(dia, "day").hour(Math.floor(inicio / 60)).minute(inicio % 60).second(0).millisecond(0);
    return {
      practica_id: "pr-sem-nueva", // el definitivo, al final (por orden de inicio)
      alumno_ids,
      type: tipo,
      start: d.toISOString(),
      end: d.add(minutos, "minute").toISOString(),
      status: "pendiente",
      instructor_id: inst,
      vehicle_id: COCHE_DE[inst],
      notas: "",
      ...extra,
    };
  };

  Object.keys(OBJETIVO).forEach((inst, k) => {
    const suyos = ALUMNOS.filter((a) => a[1] === inst).map((a) => a[0]);
    const [hIni, hFin] = HORARIO[inst];
    // 1. El circuito cerrado (si cabe).
    const [cDia, cIni, cMin, cAlumnos, cNota] = CIRCUITOS[inst];
    const circuito = nueva(inst, cDia, cIni, cMin, "ciudad", cAlumnos, { notas: cNota });
    const [c0, c1] = ms(circuito);
    if (!ocupadas(inst).some((p) => solapa(c0, c1, ...ms(p)))) out.push(circuito);
    // 2. Cada día, hasta su objetivo.
    for (let dia = 0; dia < 5; dia += 1) {
      const fecha = ymd(base.add(dia, "day").toDate());
      const delDia = () => ocupadas(inst).filter((p) => ymd(new Date(p.start)) === fecha);
      const modelo = DIAS_MODELO[(dia + k * 2) % DIAS_MODELO.length];
      let nuevasHoy = 0;
      let turno = dia * 2 + k;
      for (const [inicio, minutos, tipo] of modelo) {
        if (delDia().length >= OBJETIVO[inst][dia]) break;
        if (inicio < hIni || inicio + minutos > hFin) continue;
        const [t0, t1] = ms(nueva(inst, dia, inicio, minutos, tipo, []));
        if (ocupadas(inst).some((p) => solapa(t0, t1, ...ms(p)))) continue;
        // Alumno: el siguiente de los suyos que esté libre a esa hora y no tenga ya dos ese día.
        let alumno = null;
        for (let i = 0; i < suyos.length && !alumno; i += 1) {
          const id = suyos[(turno + i) % suyos.length];
          const hoy = [...vivas, ...out].filter((p) => p.status !== "cancelada" && alumnosDe(p).includes(id) && ymd(new Date(p.start)) === fecha).length;
          if (hoy < 2 && libreAlumno(id, t0, t1)) alumno = id;
        }
        if (!alumno) continue;
        turno += 1;
        const p = nueva(inst, dia, inicio, minutos, tipo, [alumno]);
        const n = nuevasHoy;
        const cancelada = CANCELADAS.find(([ci, cd, cn]) => ci === inst && cd === dia && cn === n);
        if (cancelada) {
          Object.assign(p, {
            status: "cancelada",
            motivo_cancelacion: cancelada[3],
            cancelada_por: cancelada[4],
            ...(cancelada[4] === "alumno" ? { cancel_origin: "whatsapp" } : {}),
            notas: cancelada[4] === "alumno" ? "Canceló por WhatsApp con el botón del recordatorio" : "",
          });
        }
        out.push(p);
        nuevasHoy += 1;
      }
    }
  });

  // Lo ya dado: casi todo con las dos firmas (una de cada ocho sin la del profesor) y, la primera de la semana de
  // Carlos, «no asistió» (solo si ya ha pasado al abrir el simulador).
  let dadas = 0;
  let plantonPuesto = false;
  out.sort((a, b) => new Date(a.start) - new Date(b.start)).forEach((p) => {
    const [t0, t1] = ms(p);
    if (p.status === "cancelada" || t1 > now) return;
    if (!plantonPuesto && p.instructor_id === "inst-001" && p.alumno_ids.length === 1) {
      plantonPuesto = true;
      Object.assign(p, { status: "no_asistio", notas: "No se presentó ni avisó" });
      return;
    }
    dadas += 1;
    const alumnoFirma = Object.fromEntries(p.alumno_ids.map((id, j) => [id, { firmado_at: new Date(t0 + (2 + j) * 60000).toISOString() }]));
    Object.assign(p, {
      status: "completada",
      firma_asistencia: alumnoFirma,
      ...(dadas % 8 === 0 ? {} : { firma_profesor: { firmado_at: new Date(t1 - 3 * 60000).toISOString(), instructor_id: p.instructor_id } }),
    });
  });
  // Ids en orden de inicio (más fáciles de leer en las pruebas).
  out.forEach((p, i) => { p.practica_id = `pr-sem-${pad(i + 1)}`; });
  return out;
}
